import React, { useState, useEffect, useRef } from 'react';
import { adsAPI, imagaeAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Button } from 'react-bootstrap';

const AdsList = () => {
    const [loading, setLoading] = useState(false);
    const [adsItems, setAdsItems] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const editPhotoRef = useRef(null);

    // Search & Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await adsAPI.getAll();
            setAdsItems(res.data.data);
        } catch (error) {
            console.error('Error fetching ads:', error);
            toast.error('Failed to load ads.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Filter & Paginate
    const filteredItems = adsItems.filter(item =>
        item.ads_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ads_owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ads_owner_phone?.includes(searchTerm) ||
        item.ads_status?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const paginatedItems = filteredItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Edit handlers
    const handleEditClick = (item) => {
        setSelectedItem({ ...item });
        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        const { name, value, type, files } = e.target;
        setSelectedItem(prev => ({
            ...prev,
            [name]: type === 'file' ? files[0] : value,
        }));
    };

    const handleUpdateItem = async () => {
        setLoading(true);
        try {
            const data = new FormData();
            Object.keys(selectedItem).forEach(key => {
                if (key !== 'ads_photo' && key !== 'id' && key !== 'created_at' && key !== 'updated_at') {
                    data.append(key, selectedItem[key]);
                }
            });
            if (selectedItem.ads_photo instanceof File) {
                data.append('ads_photo', selectedItem.ads_photo);
            }

            await adsAPI.update(selectedItem.id, data);
            toast.success('Ad updated successfully!');
            setShowEditModal(false);
            fetchItems();
        } catch (error) {
            console.error(error);
            const errors = error.response?.data?.errors;
            const msg = errors ? Object.values(errors).flat().join(' ') : 'Error updating ad.';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    // Delete handlers
    const confirmDelete = (id) => {
        toast.info(
            <div>
                <p className="mb-2">Are you sure you want to delete this ad?</p>
                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-sm btn-danger mr-2"
                        onClick={() => {
                            toast.dismiss();
                            handleDeleteItem(id);
                        }}
                    >
                        Yes
                    </button>
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => toast.dismiss()}
                    >
                        No
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeOnClick: false,
                closeButton: false,
                icon: false,
            }
        );
    };

    const handleDeleteItem = async (id) => {
        setLoading(true);
        try {
            await adsAPI.delete(id);
            toast.success('Ad deleted successfully!');
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error('Error deleting ad.');
        } finally {
            setLoading(false);
        }
    };

    // Helper for photo URL
    const getPhotoUrl = (photo) => {
        if (!photo) return null;
        if (photo.startsWith('http')) return photo;
        return `${imagaeAPI}/uploads/ads/${photo}`;
    };


    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',

        });
    };

    return (
        <>
            <div className="main-content">
                <div className="content-wrapper">
                    <div className="list-card">
                        <div className="list-card-header d-flex justify-content-between align-items-center">
                            <h5><i className="fas fa-ad me-2"></i>All Advertisements</h5>
                            <div style={{ width: '250px' }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by title, owner, phone..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="list-card-body">
                            {loading && <p>Loading...</p>}
                            {!loading && filteredItems.length === 0 && <p>No ads found.</p>}
                            {!loading && filteredItems.length > 0 && (
                                <>
                                    <div className="table-responsive">
                                        <table className="display compact table table-striped table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Title</th>
                                                    <th>Owner</th>
                                                    <th>Phone</th>
                                                    <th>Amount</th>
                                                    <th>Start</th>
                                                    <th>End</th>
                                                    <th>Photo</th>
                                                    <th>Status</th>
                                                    <th>Edit</th>
                                                    <th>Delete</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedItems.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                        <td>{item.ads_title}</td>
                                                        <td>{item.ads_owner_name}</td>
                                                        <td>{item.ads_owner_phone}</td>
                                                        <td>₹{item.ads_amount}</td>
                                                        <td>{formatDate(item.ads_start_date)}</td>

                                                        <td>{formatDate(item.ads_end_date)}</td>
                                                        <td>
                                                            {item.ads_photo ? (
                                                                <img
                                                                    src={`${imagaeAPI}/uploads/ads/${item.ads_photo}`}
                                                                    alt="Ad"
                                                                    width="50"
                                                                    height="50"
                                                                    style={{ objectFit: 'cover', cursor: 'pointer' }}
                                                                    onClick={() => window.open(`${imagaeAPI}/uploads/ads/${item.ads_photo}`, '_blank')}
                                                                />
                                                            ) : '-'}
                                                        </td>
                                                        <td>{item.ads_status}</td>
                                                        <td>
                                                            <a
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleEditClick(item);
                                                                }}
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </a>
                                                        </td>
                                                        <td>
                                                            <a
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    confirmDelete(item.id);
                                                                }}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </a>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <nav className="mt-3">
                                            <ul className="pagination justify-content-center">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </button>
                                                </li>
                                                {[...Array(totalPages).keys()].map(num => (
                                                    <li key={num + 1} className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}>
                                                        <button className="page-link" onClick={() => setCurrentPage(num + 1)}>
                                                            {num + 1}
                                                        </button>
                                                    </li>
                                                ))}
                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <ToastContainer />
                </div>
            </div>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Advertisement</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem && (
                        <form>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label>Ad Title</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="ads_title"
                                        value={selectedItem.ads_title || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label>Owner Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="ads_owner_name"
                                        value={selectedItem.ads_owner_name || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>
                            </div>
                            <div className="row g-3 mt-2">
                                <div className="col-md-4">
                                    <label>Owner Phone</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="ads_owner_phone"
                                        value={selectedItem.ads_owner_phone || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label>Owner Email</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        name="ads_owner_email"
                                        value={selectedItem.ads_owner_email || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label>Amount</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        name="ads_amount"
                                        value={selectedItem.ads_amount || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>
                            </div>
                            <div className="row g-3 mt-2">
                                <div className="col-md-6">
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="ads_start_date"
                                        value={selectedItem.ads_start_date || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="ads_end_date"
                                        value={selectedItem.ads_end_date || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>
                            </div>
                            <div className="row g-3 mt-2">
                                <div className="col-md-6">
                                    <label>Owner Address</label>
                                    <textarea
                                        className="form-control"
                                        name="ads_owner_address"
                                        value={selectedItem.ads_owner_address || ''}
                                        onChange={handleEditChange}
                                        rows="2"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <label>Status</label>
                                            <select
                                                name="ads_status"
                                                className="form-control"
                                                value={selectedItem.ads_status || 'active'}
                                                onChange={handleEditChange}
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label>Photo</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept="image/*"
                                                name="ads_photo"
                                                onChange={handleEditChange}
                                                ref={editPhotoRef}
                                            />
                                            {selectedItem.ads_photo && typeof selectedItem.ads_photo === 'string' && (
                                                <div className="mt-2">
                                                    <img
                                                        src={`${imagaeAPI}/uploads/ads/${selectedItem.ads_photo}`}
                                                        alt="Current"
                                                        width="60"
                                                    />
                                                    <p className="text-muted small">Current image</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleUpdateItem} disabled={loading}>
                        {loading ? 'Updating...' : 'Update'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AdsList;